import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormHelperText } from "@mui/material";
import Autocomplete from "@components/Autocomplete/Autocomplete.tsx";
import type { AutocompleteOption } from "@components/Autocomplete/Autocomplete.types.ts";
import DatePicker from "@components/DatePicker/DatePicker";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext.tsx";
import {
  asDateOrNull,
  DEFAULT_FIELD_LABEL_KEYS,
  isDefined,
  UPDATE_MOVEMENT_CONFIG,
  type MovementFieldKey,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/updateMovement/utils.ts";
import {
  getEarliestResourceStartTime,
  mapPilotStatusToOptions,
  type MovementError,
  type PilotStationType,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import { useUpdateMovement } from "@features/vessel-call/vessel-call-status/MovementServices/hooks/useUpdateMovement.tsx";
import type { MovementServiceDetailsBackendResponse } from "@features/vessel-call/vessel-call-status/MovementServices/preview/types";
import type { VesselCallStatusContextType } from "@features/vessel-call/vessel-call-status/VesselCallStatus.tsx";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import type { Movement, MovementUpdatePayload } from "@services/movement-services/types.ts";
import { getMovementServiceDetailsById } from "@services/movementServicesApi.ts";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toMinute } from "@utils/date-time";
import { removeNullValueKeys } from "@utils/payloadClean.ts";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

const LANG_BASE = "vessel-call.movement-services.update-form";

const PAST_OR_PRESENT_FIELDS: ReadonlyArray<MovementFieldKey> = [
  "ats",
  "anchorAweighTime",
  "anchorTime",
  "pilotUnderway",
  "pob",
  "firstLineAshore",
  "allFast",
  "pilotDisembark",
  "allClear",
  "permissionToSail",
];

const PRESENT_OR_FUTURE_FIELDS: ReadonlyArray<MovementFieldKey> = [
  "rts",
  "rta",
  "rtd",
  "eta",
  "etd",
  "ets",
  "etc",
];

type MovementFieldPayloadKey =
  | "atc"
  | "rts"
  | "rta"
  | "rtd"
  | "eta"
  | "etd"
  | "ets"
  | "etc"
  | "ats"
  | "anchorAweighTime"
  | "anchorTime"
  | "pilotUnderwayTime"
  | "pilotAwayTime"
  | "pobTime"
  | "firstLineAshoreTime"
  | "allFastTime"
  | "pilotDisembarkTime"
  | "allLinesClearTime"
  | "allClearTime"
  | "permissionToSailTime"
  | "pilotStationCode";

const MOVEMENT_FIELD_TO_PAYLOAD_KEY: Record<MovementFieldKey, MovementFieldPayloadKey> = {
  atc: "atc",
  rts: "rts",
  rta: "rta",
  rtd: "rtd",
  eta: "eta",
  etd: "etd",
  ets: "ets",
  etc: "etc",
  ats: "ats",
  anchorAweighTime: "anchorAweighTime",
  anchorTime: "anchorTime",
  pilotUnderway: "pilotUnderwayTime",
  pilotAway: "pilotAwayTime",
  pob: "pobTime",
  firstLineAshore: "firstLineAshoreTime",
  allFast: "allFastTime",
  pilotDisembark: "pilotDisembarkTime",
  allLinesClear: "allLinesClearTime",
  allClear: "allClearTime",
  permissionToSail: "permissionToSailTime",
  pilotStation: "pilotStationCode",
};

const isAfterCurrentMinute = (value: Date) => {
  const valueMinute = new Date(value);
  const nowMinute = new Date();
  valueMinute.setSeconds(0, 0);
  nowMinute.setSeconds(0, 0);
  return valueMinute.getTime() > nowMinute.getTime();
};

const isBeforeCurrentMinute = (value: Date) => {
  const valueMinute = new Date(value);
  const nowMinute = new Date();
  valueMinute.setSeconds(0, 0);
  nowMinute.setSeconds(0, 0);
  return valueMinute.getTime() < nowMinute.getTime();
};

interface UpdateMovementServiceProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  currentMovements: Movement[];
}

const UpdateMovementService: React.FC<UpdateMovementServiceProps> = ({
  isOpen,
  onClose,
  currentMovements,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();

  const { vesselCallDetails } = useOutletContext<VesselCallStatusContextType>();
  const currentMovement = currentMovements?.[0];

  const vesselCallId = vesselCallDetails?.id;
  const vesselId = vesselCallDetails?.vesselId;
  const movementServiceId = currentMovement?.id;
  const serviceVesselCall = "bff-vessel-call";

  const movementType = currentMovement?.movementType;
  const updateMovementConfig = movementType ? UPDATE_MOVEMENT_CONFIG[movementType] : undefined;
  const isSeaToAnchorage = movementType === "SEA_TO_ANCHORAGE";

  const getFieldLabel = (fieldKey: MovementFieldKey) =>
    t(updateMovementConfig?.labelKeys?.[fieldKey] ?? DEFAULT_FIELD_LABEL_KEYS[fieldKey]);

  // Fetch movement service details for prepopulation
  const { data: movementDetails, refetch: refetchMovementDetails } =
    useQuery<MovementServiceDetailsBackendResponse>({
      queryKey: ["movement-service-update-drawer", movementServiceId],
      queryFn: () =>
        getMovementServiceDetailsById({
          id: movementServiceId!,
          service: serviceVesselCall,
        }),
      enabled: !!movementServiceId && isOpen,
      staleTime: 0, // Always consider data stale to force refetch
      gcTime: 0, // Don't cache the data (React Query v5)
      refetchOnMount: "always", // Always refetch when component mounts
    });

  const etaSource = isSeaToAnchorage
    ? (movementDetails?.eta ?? vesselCallDetails?.eta)
    : movementDetails?.eta;
  const atsSource = isSeaToAnchorage
    ? (movementDetails?.ats ?? vesselCallDetails?.lastTaskDecision?.timestamp)
    : movementDetails?.ats;
  const earliestResourceStartTime = useMemo(
    () => getEarliestResourceStartTime(movementDetails?.additionalResources),
    [movementDetails?.additionalResources],
  );

  // Refetch movement details when drawer opens to get latest updates
  useEffect(() => {
    if (isOpen && movementServiceId) {
      // Reset form first, then refetch to ensure clean state
      resetFormValues();
      // Force refetch with fresh data (bypass cache)
      void refetchMovementDetails({ cancelRefetch: false });
    }
  }, [isOpen, movementServiceId, refetchMovementDetails]);

  const [atc, setAtc] = useState<Date | null>(null);
  const [rts, setRts] = useState<Date | null>(null);
  const [rta, setRta] = useState<Date | null>(null);
  const [rtd, setRtd] = useState<Date | null>(null);
  const [eta, setEta] = useState<Date | null>(null);
  const [etd, setEtd] = useState<Date | null>(null);
  const [ets, setEts] = useState<Date | null>(null);
  const [etc, setEtc] = useState<Date | null>(null);
  const [ats, setAts] = useState<Date | null>(null);
  const [anchorTime, setAnchorTime] = useState<Date | null>(null);
  const [anchorAweighTime, setAnchorAweighTime] = useState<Date | null>(null);
  const [pilotUnderway, setPilotUnderway] = useState<Date | null>(null);
  const [pob, setPob] = useState<Date | null>(null);
  const [allLinesClear, setAllLinesClear] = useState<Date | null>(null);
  const [allClear, setAllClear] = useState<Date | null>(null);
  const [pilotAway, setPilotAway] = useState<Date | null>(null);
  const [firstLineAshore, setFirstLineAshore] = useState<Date | null>(null);
  const [allFast, setAllFast] = useState<Date | null>(null);
  const [pilotDisembark, setPilotDisembark] = useState<Date | null>(null);
  const [permissionToSail, setPermissionToSail] = useState<Date | null>(null);
  const [pilotStation, setPilotStation] = useState<AutocompleteOption | null>(null);
  const [rtsError, setRtsError] = useState<string | null>(null);
  const [atsError, setAtsError] = useState<string | null>(null);
  const [etsError, setEtsError] = useState<string | null>(null);
  const [etcError, setEtcError] = useState<string | null>(null);
  const [pobError, setPobError] = useState<string | null>(null);
  const [allFastError, setAllFastError] = useState<string | null>(null);
  const [pilotDisembarkError, setPilotDisembarkError] = useState<string | null>(null);
  const [anchorTimeError, setAnchorTimeError] = useState<string | null>(null);
  const [dateConstraintErrors, setDateConstraintErrors] = useState<
    Partial<Record<MovementFieldKey, string>>
  >({});
  const hasUserEditsRef = useRef(false);

  const markAsEdited = () => {
    hasUserEditsRef.current = true;
  };

  const getDateConstraint = (fieldKey: MovementFieldKey) => {
    if (PAST_OR_PRESENT_FIELDS.includes(fieldKey)) return "not-future" as const;
    if (PRESENT_OR_FUTURE_FIELDS.includes(fieldKey)) return "not-past" as const;
    return undefined;
  };

  const getDateError = (fieldKey: MovementFieldKey, validationError?: string | null) =>
    dateConstraintErrors[fieldKey] ?? validationError;

  const getDateConstraintError = (fieldKey: MovementFieldKey, value: Date | null) => {
    let error: string | undefined;
    if (value && PAST_OR_PRESENT_FIELDS.includes(fieldKey) && isAfterCurrentMinute(value)) {
      error = t("form-validation.must-not-be-in-future", { label: getFieldLabel(fieldKey) });
    }
    if (value && PRESENT_OR_FUTURE_FIELDS.includes(fieldKey) && isBeforeCurrentMinute(value)) {
      error = t("form-validation.must-not-be-in-past", { label: getFieldLabel(fieldKey) });
    }
    return error;
  };

  const validateDateConstraint = (fieldKey: MovementFieldKey, value: Date | null) => {
    const error = getDateConstraintError(fieldKey, value);
    setDateConstraintErrors((currentErrors) => {
      if (currentErrors[fieldKey] === error) return currentErrors;
      return { ...currentErrors, [fieldKey]: error };
    });
  };

  const resetFormValues = () => {
    hasUserEditsRef.current = false;
    setRts(null);
    setRta(null);
    setRtd(null);
    setEta(null);
    setEtd(null);
    setEts(null);
    setEtc(null);
    setAts(null);
    setAnchorTime(null);
    setAnchorAweighTime(null);
    setPilotUnderway(null);
    setPob(null);
    setAllLinesClear(null);
    setAllClear(null);
    setPilotAway(null);
    setFirstLineAshore(null);
    setAllFast(null);
    setPilotDisembark(null);
    setPermissionToSail(null);
    setPilotStation(null);
    setRtsError(null);
    setAtsError(null);
    setEtsError(null);
    setEtcError(null);
    setPobError(null);
    setAllFastError(null);
    setPilotDisembarkError(null);
    setAnchorTimeError(null);
    setDateConstraintErrors({});
  };

  const { options: stations } = useFormattedReference<PilotStationType>(
    REFERENCE_DATA_TYPES.PILOT_STATIONS,
    "bff-vessel-call",
  );

  const pilotStationOptions = useMemo(() => mapPilotStatusToOptions(stations), [stations]);

  // Prepopulate fields from movement details when drawer opens
  useEffect(() => {
    if (!movementDetails || !isOpen || hasUserEditsRef.current) return;

    // Helper function to safely parse date
    const parseDate = (dateValue: string | null | undefined): Date | null => {
      if (!dateValue) return null;
      try {
        const date = new Date(dateValue);
        return !isNaN(date.getTime()) ? date : null;
      } catch {
        return null;
      }
    };

    const details = movementDetails;

    // Always set these fields, even if null, to properly reset cleared values
    setRts(parseDate(details.rts));
    setRta(parseDate(details.rta));
    setRtd(parseDate(details.rtd));
    setEta(parseDate(etaSource));
    setEtd(parseDate(details.etd));
    setEts(parseDate(details.ets));
    setEtc(parseDate(details.etc));
    setAts(parseDate(atsSource) ?? earliestResourceStartTime);
    setAnchorTime(parseDate(details.anchorTime));
    setAnchorAweighTime(parseDate(details.anchorAweighTime));
    setPilotUnderway(parseDate(details.pilotUnderwayTime));
    setPob(parseDate(details.pobTime));
    setPilotAway(parseDate(details.pilotAwayTime));
    setFirstLineAshore(parseDate(details.firstLineAshoreTime));
    setAllFast(parseDate(details.allFastTime));
    setPilotDisembark(parseDate(details.pilotDisembarkTime));
    setPermissionToSail(parseDate(details.permissionToSailTime));
    setAllClear(parseDate(details.allClearTime));
    setAllLinesClear(parseDate(details.allLinesClearTime));

    // Prepopulate pilot station - explicitly set to null if not present
    if (details.pilotStationCode) {
      const stationOption = pilotStationOptions.find(
        (opt) => opt.value === details.pilotStationCode,
      );
      setPilotStation(stationOption || null);
    } else {
      setPilotStation(null);
    }
  }, [
    movementDetails,
    isOpen,
    pilotStationOptions,
    isSeaToAnchorage,
    atsSource,
    vesselCallDetails?.eta,
    earliestResourceStartTime,
  ]);

  const handleSuccess = async () => {
    // Invalidate queries immediately to ensure fresh data
    setTimeout(async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
      });
    }, 2000);

    if (currentMovement?.id) {
      setTimeout(async () => {
        await queryClient.invalidateQueries({
          queryKey: ["movement-service-preview", currentMovement.id],
        });
        // Invalidate the drawer query to refetch fresh data when drawer reopens
        await queryClient.invalidateQueries({
          queryKey: ["movement-service-update-drawer", currentMovement.id],
        });
      }, 2000);
    }
    resetFormValues();
    onClose("updateDrawer");
    notify({
      title: t("vessel-call.movement-services.update-form.movement-updated-success-title"),
      description: t(
        "vessel-call.movement-services.update-form.movement-updated-success-description",
      ),
      type: "success",
    });
  };

  const handleCloseDrawer = () => {
    resetFormValues();
    onClose("updateDrawer");
  };

  const handleError = async (error: MovementError) => {
    await queryClient.invalidateQueries({
      queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
    });

    if (currentMovement?.id) {
      await queryClient.invalidateQueries({
        queryKey: ["movement-service-preview", currentMovement.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["movement-service-update-drawer", currentMovement.id],
      });
    }
    notify({
      title: t("vessel-call.movement-services.update-form.movement-updated-error-title"),
      description:
        error?.response?.data?.detail ??
        error?.message ??
        t("vessel-call.movement-services.update-form.movement-updated-error-description"),
      type: "error",
    });
    if (error.status === 409) {
      handleCloseDrawer();
    }
  };

  const { updateMovement } = useUpdateMovement({
    handleSuccess,
    handleError,
  });

  const handleSubmit = async () => {
    if (!currentMovement || !updateMovementConfig) {
      return;
    }

    setRtsError(null);

    const atc = movementDetails?.atc ? new Date(movementDetails.atc) : null;
    const fieldValues: Record<MovementFieldKey, Date | string | null> = {
      atc,
      rts,
      rta,
      rtd,
      eta,
      etd,
      ets,
      etc,
      ats,
      anchorAweighTime,
      anchorTime,
      pilotUnderway,
      pilotAway,
      pob,
      firstLineAshore,
      allFast,
      pilotDisembark,
      allLinesClear,
      allClear,
      permissionToSail,
      pilotStation: (pilotStation?.value as string | undefined) ?? null,
    };
    const submitDateErrors: Partial<Record<MovementFieldKey, string>> = {};
    updateMovementConfig.visibleFields.forEach((fieldKey) => {
      const value = fieldValues[fieldKey];
      if (!(value instanceof Date)) return;
      const error = getDateConstraintError(fieldKey, value);
      if (error) submitDateErrors[fieldKey] = error;
    });
    setDateConstraintErrors(submitDateErrors);

    let hasDateOrderError = false;
    const hasVisibleFields = (...fieldKeys: MovementFieldKey[]) =>
      fieldKeys.every((fieldKey) => updateMovementConfig?.visibleFields.includes(fieldKey));
    const setMustBeAfterError = (
      setError: React.Dispatch<React.SetStateAction<string | null>>,
      subject: MovementFieldKey,
      object: MovementFieldKey,
    ) => {
      setError(
        t("form-validation.must-be-after", {
          subject: getFieldLabel(subject),
          object: getFieldLabel(object),
        }),
      );
      hasDateOrderError = true;
    };

    setAllFastError(null);
    setAtsError(null);
    setEtsError(null);
    setEtcError(null);
    setPobError(null);
    setPilotDisembarkError(null);
    setAnchorTimeError(null);

    if (hasVisibleFields("ets") && ets && etc && ets.getTime() > etc.getTime()) {
      setEtsError(
        t("form-validation.must-be-before-or-equal", {
          subject: getFieldLabel("ets"),
          object: getFieldLabel("etc"),
        }),
      );
      hasDateOrderError = true;
    }

    if (
      updateMovementConfig?.visibleFields.includes("ats") &&
      ats &&
      atc &&
      !isNaN(atc.getTime()) &&
      ats.getTime() > atc.getTime()
    ) {
      setAtsError(
        t("form-validation.must-be-equal-or-before-value", {
          label: getFieldLabel("ats"),
          value: getFieldLabel("atc"),
        }),
      );
      hasDateOrderError = true;
    }

    const normalizedAts = toMinute(ats);
    const normalizedEarliestResourceStartTime = toMinute(earliestResourceStartTime);
    if (
      updateMovementConfig?.visibleFields.includes("ats") &&
      normalizedAts &&
      normalizedEarliestResourceStartTime &&
      normalizedAts.getTime() > normalizedEarliestResourceStartTime.getTime()
    ) {
      setAtsError(
        t("form-validation.ats-before-earliest-resource-start", {
          label: getFieldLabel("ats"),
        }),
      );
      hasDateOrderError = true;
    }

    if (
      hasVisibleFields("pilotUnderway", "pob") &&
      pilotUnderway &&
      pob &&
      pilotUnderway.getTime() > pob.getTime()
    ) {
      setMustBeAfterError(setPobError, "pob", "pilotUnderway");
    }

    if (
      hasVisibleFields("firstLineAshore", "allFast") &&
      firstLineAshore &&
      allFast &&
      firstLineAshore.getTime() > allFast.getTime()
    ) {
      setMustBeAfterError(setAllFastError, "allFast", "firstLineAshore");
    }

    if (
      hasVisibleFields("allClear", "allFast") &&
      allClear &&
      allFast &&
      allClear.getTime() > allFast.getTime()
    ) {
      setMustBeAfterError(setAllFastError, "allFast", "allClear");
    }

    if (
      hasVisibleFields("allClear", "anchorTime") &&
      allClear &&
      anchorTime &&
      allClear.getTime() > anchorTime.getTime()
    ) {
      setMustBeAfterError(setAnchorTimeError, "anchorTime", "allClear");
    }

    if (
      hasVisibleFields("pob", "pilotDisembark") &&
      pob &&
      pilotDisembark &&
      pob.getTime() > pilotDisembark.getTime()
    ) {
      setMustBeAfterError(setPilotDisembarkError, "pilotDisembark", "pob");
    }

    if (
      hasVisibleFields("allFast", "pilotDisembark") &&
      allFast &&
      pilotDisembark &&
      allFast.getTime() > pilotDisembark.getTime()
    ) {
      setMustBeAfterError(setPilotDisembarkError, "pilotDisembark", "allFast");
    }

    if (hasDateOrderError || Object.keys(submitDateErrors).length > 0) {
      return;
    }

    const payload = updateMovementConfig.visibleFields.reduce<MovementUpdatePayload>(
      (result, fieldKey) => {
        const value = fieldValues[fieldKey];
        result[MOVEMENT_FIELD_TO_PAYLOAD_KEY[fieldKey]] =
          value instanceof Date ? value.toISOString() : value;
        return result;
      },
      { status: "UPDATED" },
    );

    const mappedPayload = removeNullValueKeys(payload);

    if (!currentMovement.id) {
      return;
    }

    await updateMovement({
      id: currentMovement.id,
      service: "bff-vessel-call",
      payload: mappedPayload,
    });
  };

  const fieldComponents: Record<MovementFieldKey, React.ReactElement> = {
    atc: (
      <div key="atc">
        <CustomFormLabel data-testid="atc-labelId">{getFieldLabel("atc")}</CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(atc)}
          onChange={(d) => setAtc(d)}
          testId={`atc-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            minHeight: "20px",
          }}
        ></FormHelperText>
      </div>
    ),
    rts: (
      <div key="rts">
        <CustomFormLabel data-testid="rts-labelId">{getFieldLabel("rts")}</CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("rts", rtsError))}
          value={asDateOrNull(rts)}
          onChange={(d) => {
            markAsEdited();
            setRts(d);
            validateDateConstraint("rts", d);
            if (rtsError) {
              setRtsError(null);
            }
          }}
          testId={`rts-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("rts")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("rts", rtsError) ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("rts", rtsError)}
        </FormHelperText>
      </div>
    ),
    rta: (
      <div key="rta">
        <CustomFormLabel data-testid="rta-labelId">{getFieldLabel("rta")}</CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("rta"))}
          value={asDateOrNull(rta)}
          onChange={(d) => {
            markAsEdited();
            setRta(d);
            validateDateConstraint("rta", d);
          }}
          testId={`rta-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("rta")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("rta") ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("rta")}
        </FormHelperText>
      </div>
    ),
    rtd: (
      <div key="rtd">
        <CustomFormLabel data-testid="rtd-labelId">{getFieldLabel("rtd")}</CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("rtd"))}
          value={asDateOrNull(rtd)}
          onChange={(d) => {
            markAsEdited();
            setRtd(d);
            validateDateConstraint("rtd", d);
          }}
          testId={`rtd-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("rtd")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("rtd") ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("rtd")}
        </FormHelperText>
      </div>
    ),
    eta: (
      <div key="eta">
        <CustomFormLabel data-testid="eta-labelId">{getFieldLabel("eta")}</CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("eta"))}
          value={asDateOrNull(eta)}
          onChange={(d) => {
            markAsEdited();
            setEta(d);
            validateDateConstraint("eta", d);
          }}
          testId={`eta-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("eta")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("eta") ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("eta")}
        </FormHelperText>
      </div>
    ),
    etd: (
      <div key="etd">
        <CustomFormLabel data-testid="etd-labelId">{getFieldLabel("etd")}</CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("etd"))}
          value={asDateOrNull(etd)}
          onChange={(d) => {
            markAsEdited();
            setEtd(d);
            validateDateConstraint("etd", d);
          }}
          testId={`etd-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("etd")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("etd") ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("etd")}
        </FormHelperText>
      </div>
    ),
    ets: (
      <div key="ets">
        <CustomFormLabel data-testid="ets-labelId">{getFieldLabel("ets")}</CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("ets", etsError))}
          value={asDateOrNull(ets)}
          onChange={(d) => {
            markAsEdited();
            setEts(d);
            validateDateConstraint("ets", d);
            if (etsError) {
              setEtsError(null);
            }
          }}
          testId={`ets-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("ets")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("ets", etsError) ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("ets", etsError)}
        </FormHelperText>
      </div>
    ),
    etc: (
      <div key="etc">
        <CustomFormLabel data-testid="etc-labelId">{getFieldLabel("etc")}</CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("etc", etcError))}
          value={asDateOrNull(etc)}
          onChange={(d) => {
            markAsEdited();
            setEtc(d);
            validateDateConstraint("etc", d);
            if (etcError) {
              setEtcError(null);
            }
          }}
          testId={`etc-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("etc")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("etc", etcError) ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("etc", etcError)}
        </FormHelperText>
      </div>
    ),
    ats: (
      <div key="ats">
        <CustomFormLabel data-testid="ats-labelId">{getFieldLabel("ats")}</CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("ats", atsError))}
          value={asDateOrNull(ats)}
          onChange={(d) => {
            markAsEdited();
            setAts(d);
            validateDateConstraint("ats", d);
            if (atsError) {
              setAtsError(null);
            }
          }}
          testId={`ats-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("ats")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("ats", atsError) ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("ats", atsError)}
        </FormHelperText>
      </div>
    ),
    anchorAweighTime: (
      <div key="anchorAweigh">
        <CustomFormLabel data-testid="anchorAweighTime-labelId">
          {getFieldLabel("anchorAweighTime")}
        </CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("anchorAweighTime"))}
          value={asDateOrNull(anchorAweighTime)}
          onChange={(d) => {
            markAsEdited();
            setAnchorAweighTime(d);
            validateDateConstraint("anchorAweighTime", d);
          }}
          testId={`anchorAweighTime-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("anchorAweighTime")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("anchorAweighTime") ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("anchorAweighTime")}
        </FormHelperText>
      </div>
    ),
    anchorTime: (
      <div key="anchorTime">
        <CustomFormLabel data-testid="anchorTime-labelId">
          {getFieldLabel("anchorTime")}
        </CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("anchorTime", anchorTimeError))}
          value={asDateOrNull(anchorTime)}
          onChange={(d) => {
            markAsEdited();
            setAnchorTime(d);
            validateDateConstraint("anchorTime", d);
            if (anchorTimeError) {
              setAnchorTimeError(null);
            }
          }}
          testId={`anchorTime-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("anchorTime")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("anchorTime", anchorTimeError) ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("anchorTime", anchorTimeError)}
        </FormHelperText>
      </div>
    ),
    pilotUnderway: (
      <div key="pilotUnderway">
        <CustomFormLabel data-testid="pilotUnderway-labelId">
          {getFieldLabel("pilotUnderway")}
        </CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("pilotUnderway"))}
          value={asDateOrNull(pilotUnderway)}
          onChange={(d) => {
            markAsEdited();
            setPilotUnderway(d);
            validateDateConstraint("pilotUnderway", d);
          }}
          testId={`pilotUnderway-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("pilotUnderway")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("pilotUnderway") ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("pilotUnderway")}
        </FormHelperText>
      </div>
    ),
    pilotAway: (
      <div key="pilotAway">
        <CustomFormLabel data-testid="pilotAway-labelId">
          {getFieldLabel("pilotAway")}
        </CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={false}
          value={asDateOrNull(pilotAway)}
          onChange={(d) => {
            markAsEdited();
            setPilotAway(d);
          }}
          testId={`pilotAway-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            minHeight: "20px",
          }}
        ></FormHelperText>
      </div>
    ),
    pob: (
      <div key="pob">
        <CustomFormLabel data-testid="pob-labelId">{getFieldLabel("pob")}</CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("pob", pobError))}
          value={asDateOrNull(pob)}
          onChange={(d) => {
            markAsEdited();
            setPob(d);
            validateDateConstraint("pob", d);
            if (pobError) {
              setPobError(null);
            }
          }}
          testId={`pob-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("pob")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("pob", pobError) ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("pob", pobError)}
        </FormHelperText>
      </div>
    ),
    firstLineAshore: (
      <div key="firstLineAshore">
        <CustomFormLabel data-testid="firstLineAshore-labelId">
          {getFieldLabel("firstLineAshore")}
        </CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("firstLineAshore"))}
          value={asDateOrNull(firstLineAshore)}
          onChange={(d) => {
            markAsEdited();
            setFirstLineAshore(d);
            validateDateConstraint("firstLineAshore", d);
          }}
          testId={`firstLineAshore-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("firstLineAshore")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("firstLineAshore") ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("firstLineAshore")}
        </FormHelperText>
      </div>
    ),
    allFast: (
      <div key="allFast">
        <CustomFormLabel data-testid="allFast-labelId">{getFieldLabel("allFast")}</CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("allFast", allFastError))}
          value={asDateOrNull(allFast)}
          onChange={(d) => {
            markAsEdited();
            setAllFast(d);
            validateDateConstraint("allFast", d);
            if (allFastError) {
              setAllFastError(null);
            }
          }}
          testId={`allFast-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("allFast")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("allFast", allFastError) ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("allFast", allFastError)}
        </FormHelperText>
      </div>
    ),
    pilotDisembark: (
      <div key="pilotDisembark">
        <CustomFormLabel data-testid="pilotDisembark-labelId">
          {getFieldLabel("pilotDisembark")}
        </CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("pilotDisembark", pilotDisembarkError))}
          value={asDateOrNull(pilotDisembark)}
          onChange={(d) => {
            markAsEdited();
            setPilotDisembark(d);
            validateDateConstraint("pilotDisembark", d);
            if (pilotDisembarkError) {
              setPilotDisembarkError(null);
            }
          }}
          testId={`pilotDisembark-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("pilotDisembark")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("pilotDisembark", pilotDisembarkError) ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("pilotDisembark", pilotDisembarkError)}
        </FormHelperText>
      </div>
    ),
    allLinesClear: (
      <div key="allLinesClear">
        <CustomFormLabel data-testid="allLinesClear-labelId">
          {getFieldLabel("allLinesClear")}
        </CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={false}
          value={asDateOrNull(allLinesClear)}
          onChange={(d) => {
            markAsEdited();
            setAllLinesClear(d);
          }}
          testId={`allLinesClear-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            minHeight: "20px",
          }}
        ></FormHelperText>
      </div>
    ),
    allClear: (
      <div key="allClear">
        <CustomFormLabel data-testid="allClear-labelId">
          {getFieldLabel("allClear")}
        </CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("allClear"))}
          value={asDateOrNull(allClear)}
          onChange={(d) => {
            markAsEdited();
            setAllClear(d);
            validateDateConstraint("allClear", d);
          }}
          testId={`allClear-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("allClear")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("allClear") ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("allClear")}
        </FormHelperText>
      </div>
    ),
    permissionToSail: (
      <div key="permissionToSail">
        <CustomFormLabel data-testid="pilotDisembark-labelId">
          {getFieldLabel("permissionToSail")}
        </CustomFormLabel>
        <DatePicker
          quickCapture
          hasError={Boolean(getDateError("permissionToSail"))}
          value={asDateOrNull(permissionToSail)}
          onChange={(d) => {
            markAsEdited();
            setPermissionToSail(d);
            validateDateConstraint("permissionToSail", d);
          }}
          testId={`permissionToSail-date-input`}
          combinedInput
          dateConstraint={getDateConstraint("permissionToSail")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: getDateError("permissionToSail") ? "visible" : "hidden",
            minHeight: "20px",
          }}
        >
          {getDateError("permissionToSail")}
        </FormHelperText>
      </div>
    ),
    pilotStation: (
      <div key="pilotStation">
        <CustomFormLabel data-testid="pilotStation-labelId">
          {getFieldLabel("pilotStation")}
        </CustomFormLabel>
        <Autocomplete
          options={pilotStationOptions}
          value={pilotStation}
          onChange={(opt) => {
            markAsEdited();
            setPilotStation(opt);
          }}
          testId={`pilotStation-autocomplete`}
          placeholder={getFieldLabel("pilotStation")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            minHeight: "20px",
          }}
        ></FormHelperText>
      </div>
    ),
  };

  if (!currentMovement) {
    return null;
  }

  const formFields = (updateMovementConfig?.visibleFields ?? []).map(
    (fieldKey) => fieldComponents[fieldKey],
  );
  const visibleFields = formFields.filter(isDefined);

  return (
    <div data-testid="update-movement-service-form">
      <SideDrawer
        isOpen={isOpen}
        title={t(`${LANG_BASE}.title`)}
        onClose={handleCloseDrawer}
        submitLabel={t(`${LANG_BASE}.button`)}
        onSubmit={handleSubmit}
        width={800}
      >
        <div className="p-6">
          <div className="col-span-2 grid grid-cols-2 gap-x-6 gap-y-2">{visibleFields}</div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default UpdateMovementService;
